import React, { FC, useEffect, useMemo, useState } from "react";

type SessionTimeoutTimerProps = {
  expiresAt?: number | null; // unix timestamp in seconds
};

const formatCountdown = (totalSeconds: number) => {
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hh = `${hours}`.padStart(2, "0");
  const mm = `${minutes}`.padStart(2, "0");
  const ss = `${seconds}`.padStart(2, "0");

  if (days > 0) {
    return `${days}d ${hh}:${mm}:${ss}`;
  }

  return `${hh}:${mm}:${ss}`;
};

const SessionTimeoutTimer: FC<SessionTimeoutTimerProps> = ({ expiresAt }) => {
  const [nowInSeconds, setNowInSeconds] = useState<number>(() =>
    Math.floor(Date.now() / 1000)
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowInSeconds(Math.floor(Date.now() / 1000));
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const remainingSeconds = useMemo(() => {
    if (!expiresAt || expiresAt <= 0) {
      return null;
    }
    return Math.max(expiresAt - nowInSeconds, 0);
  }, [expiresAt, nowInSeconds]);

  const valusIsMoreThan24H = useMemo(() => {
    if (!expiresAt || expiresAt <= 0) {
      return null;
    }
    return expiresAt - nowInSeconds > 24 * 3600;
  }, [expiresAt, nowInSeconds]);

  const displayValue = useMemo(() => {
    if (remainingSeconds === null) {
      return "Unavailable";
    }
    if (remainingSeconds <= 0) {
      return "Expired";
    }
    return formatCountdown(remainingSeconds);
  }, [remainingSeconds]);

  return (
    <div className="mb-2 bg-gray-100">
      <div className="flex items-center justify-between px-6 py-3 border-y border-y-gray-300 text-sm text-heading-gray888">
        <span className="">
          {valusIsMoreThan24H ? (
            <>
              Session
              <br />
              ends in
            </>
          ) : (
            <>Session ends in</>
          )}
        </span>
        <span className="font-semibold">{displayValue}</span>
      </div>
    </div>
  );
};

export default SessionTimeoutTimer;
